import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Package, DollarSign, ShoppingBag, Tag, MapPin, TruckIcon, Search, Filter, X, MessageCircle, Box, Users } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pickupLocations, setPickupLocations] = useState([]);
  const [whatsappLeads, setWhatsappLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ totalProducts: 0, totalOrders: 0, totalRevenue: 0 });
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [leadConfirm, setLeadConfirm] = useState({ open: false, type: null, leadId: null });
  const [editingUser, setEditingUser] = useState(null);
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const [isLeadDialogOpen, setIsLeadDialogOpen] = useState(false);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image: '',
    stock: '',
    featured: false,
    discount_percentage: '0'
  });
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: ''
  });
  const [orderForm, setOrderForm] = useState({
    status: '',
    tracking_number: ''
  });
  const [locationForm, setLocationForm] = useState({
    name: '',
    address: '',
    city: '',
    phone: '',
    active: true
  });
  const [leadForm, setLeadForm] = useState({
    items: [],
    notes: ''
  });
  const [userForm, setUserForm] = useState({
    email: '',
    name: '',
    password: '',
    role: 'user'
  });
  
  // Product filters
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');
  const [showOnlyDiscounted, setShowOnlyDiscounted] = useState(false);
  const [showOnlyFeatured, setShowOnlyFeatured] = useState(false);
  const [showLowStock, setShowLowStock] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Order filters
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderDeliveryTypeFilter, setOrderDeliveryTypeFilter] = useState('all');
  const [orderDateFrom, setOrderDateFrom] = useState('');
  const [orderDateTo, setOrderDateTo] = useState('');
  const [filteredOrders, setFilteredOrders] = useState([]);

  // User filters
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Location filters
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [locationStatusFilter, setLocationStatusFilter] = useState('all');
  const [filteredLocations, setFilteredLocations] = useState([]);

  // WhatsApp lead filters
  const [leadSearchTerm, setLeadSearchTerm] = useState('');
  const [leadStatusFilter, setLeadStatusFilter] = useState('all');
  const [leadDateFrom, setLeadDateFrom] = useState('');
  const [leadDateTo, setLeadDateTo] = useState('');
  const [filteredLeads, setFilteredLeads] = useState([]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchOrders();
    fetchPickupLocations();
    fetchWhatsAppLeads();
    fetchUsers();
  }, []);

  useEffect(() => {
    applyProductFilters();
  }, [products, productSearchTerm, productCategoryFilter, showOnlyDiscounted, showOnlyFeatured, showLowStock]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
      setStats(prev => ({ ...prev, totalProducts: response.data.length }));
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`);
      setOrders(response.data);
      const revenue = response.data.reduce((sum, order) => sum + order.total, 0);
      setStats(prev => ({ ...prev, totalOrders: response.data.length, totalRevenue: revenue }));
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchPickupLocations = async () => {
    try {
      const response = await axios.get(`${API}/admin/pickup-locations`);
      setPickupLocations(response.data);
    } catch (error) {
      console.error('Error fetching pickup locations:', error);
    }
  };

  const applyProductFilters = () => {
    let filtered = [...products];

    // Search filter
    if (productSearchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(productSearchTerm.toLowerCase())
      );
    }

    // Category filter
    if (productCategoryFilter !== 'all') {
      filtered = filtered.filter(product => product.category === productCategoryFilter);
    }

    // Discounted filter
    if (showOnlyDiscounted) {
      filtered = filtered.filter(product => product.discount_percentage > 0);
    }

    // Featured filter
    if (showOnlyFeatured) {
      filtered = filtered.filter(product => product.featured);
    }

    // Low stock filter (less than 10)
    if (showLowStock) {
      filtered = filtered.filter(product => product.stock < 10);
    }

    setFilteredProducts(filtered);
  };

  const clearProductFilters = () => {
    setProductSearchTerm('');
    setProductCategoryFilter('all');
    setShowOnlyDiscounted(false);
    setShowOnlyFeatured(false);
    setShowLowStock(false);
  };

  const activeProductFiltersCount = () => {
    let count = 0;
    if (productSearchTerm) count++;
    if (productCategoryFilter !== 'all') count++;
    if (showOnlyDiscounted) count++;
    if (showOnlyFeatured) count++;
    if (showLowStock) count++;
    return count;
  };
  // Order filters functions
  const applyOrderFilters = () => {
    let filtered = [...orders];

    // Filter by search term (customer email or name)
    if (orderSearchTerm) {
      const searchLower = orderSearchTerm.toLowerCase();
      filtered = filtered.filter(order => {
        const email = (order.guest_email || '').toLowerCase();
        const name = (order.guest_name || '').toLowerCase();
        const orderId = order.id.toLowerCase();
        return email.includes(searchLower) || name.includes(searchLower) || orderId.includes(searchLower);
      });
    }

    // Filter by status
    if (orderStatusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === orderStatusFilter);
    }

    // Filter by delivery type
    if (orderDeliveryTypeFilter !== 'all') {
      filtered = filtered.filter(order => order.delivery_type === orderDeliveryTypeFilter);
    }

    // Filter by date range
    if (orderDateFrom) {
      const fromDate = new Date(orderDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate >= fromDate;
      });
    }

    if (orderDateTo) {
      const toDate = new Date(orderDateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate <= toDate;
      });
    }

    setFilteredOrders(filtered);
  };

  useEffect(() => {
    applyOrderFilters();
  }, [orderSearchTerm, orderStatusFilter, orderDeliveryTypeFilter, orderDateFrom, orderDateTo, orders]);

  const clearOrderFilters = () => {
    setOrderSearchTerm('');
    setOrderStatusFilter('all');
    setOrderDeliveryTypeFilter('all');
    setOrderDateFrom('');
    setOrderDateTo('');
  };

  const activeOrderFiltersCount = () => {
    let count = 0;
    if (orderSearchTerm) count++;
    if (orderStatusFilter !== 'all') count++;
    if (orderDeliveryTypeFilter !== 'all') count++;
    if (orderDateFrom) count++;
    if (orderDateTo) count++;
    return count;
  };

  // ===== Location filters =====
  const applyLocationFilters = () => {
    let filtered = [...pickupLocations];
    if (locationSearchTerm) {
      const s = locationSearchTerm.toLowerCase();
      filtered = filtered.filter(loc =>
        (loc.name || '').toLowerCase().includes(s) ||
        (loc.address || '').toLowerCase().includes(s) ||
        (loc.city || '').toLowerCase().includes(s) ||
        (loc.phone || '').toLowerCase().includes(s)
      );
    }
    if (locationStatusFilter !== 'all') {
      const wantActive = locationStatusFilter === 'active';
      filtered = filtered.filter(loc => !!loc.active === wantActive);
    }
    setFilteredLocations(filtered);
  };

  useEffect(() => {
    applyLocationFilters();
  }, [locationSearchTerm, locationStatusFilter, pickupLocations]);

  const clearLocationFilters = () => {
    setLocationSearchTerm('');
    setLocationStatusFilter('all');
  };

  const activeLocationFiltersCount = () => {
    let count = 0;
    if (locationSearchTerm) count++;
    if (locationStatusFilter !== 'all') count++;
    return count;
  };

  // ===== WhatsApp lead filters =====
  const applyLeadFilters = () => {
    let filtered = [...whatsappLeads];
    if (leadSearchTerm) {
      const s = leadSearchTerm.toLowerCase();
      filtered = filtered.filter(lead =>
        (lead.customer_name || '').toLowerCase().includes(s) ||
        (lead.customer_email || '').toLowerCase().includes(s) ||
        (lead.customer_whatsapp || '').toLowerCase().includes(s)
      );
    }
    if (leadStatusFilter !== 'all') {
      filtered = filtered.filter(lead => lead.status === leadStatusFilter);
    }
    if (leadDateFrom) {
      const fromDate = new Date(leadDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(lead => new Date(lead.created_at) >= fromDate);
    }
    if (leadDateTo) {
      const toDate = new Date(leadDateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(lead => new Date(lead.created_at) <= toDate);
    }
    setFilteredLeads(filtered);
  };

  useEffect(() => {
    applyLeadFilters();
  }, [leadSearchTerm, leadStatusFilter, leadDateFrom, leadDateTo, whatsappLeads]);

  const clearLeadFilters = () => {
    setLeadSearchTerm('');
    setLeadStatusFilter('all');
    setLeadDateFrom('');
    setLeadDateTo('');
  };

  const activeLeadFiltersCount = () => {
    let count = 0;
    if (leadSearchTerm) count++;
    if (leadStatusFilter !== 'all') count++;
    if (leadDateFrom) count++;
    if (leadDateTo) count++;
    return count;
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        stock: parseInt(productForm.stock),
        discount_percentage: parseFloat(productForm.discount_percentage)
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, data);
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/admin/products`, data);
        toast.success('Producto creado');
      }

      setIsProductDialogOpen(false);
      resetProductForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Error al guardar el producto');
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, categoryForm);
        toast.success('Categoría actualizada');
      } else {
        await axios.post(`${API}/admin/categories`, categoryForm);
        toast.success('Categoría creada');
      }

      setIsCategoryDialogOpen(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar categoría');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Producto eliminado');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Error al eliminar el producto');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta categoría?')) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`);
      toast.success('Categoría eliminada');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar categoría');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image: product.image,
      stock: product.stock.toString(),
      featured: product.featured,
      discount_percentage: (product.discount_percentage || 0).toString()
    });
    setIsProductDialogOpen(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || ''
    });
    setIsCategoryDialogOpen(true);
  };

  const resetProductForm = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: '',
      category: '',
      image: '',
      stock: '',
      featured: false,
      discount_percentage: '0'
    });
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      description: ''
    });
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/orders/${editingOrder.id}/status`, orderForm);
      toast.success('Estado de orden actualizado');
      setIsOrderDialogOpen(false);
      resetOrderForm();
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar orden');
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setOrderForm({
      status: order.status,
      tracking_number: order.tracking_number || ''
    });
    setIsOrderDialogOpen(true);
  };

  const resetOrderForm = () => {
    setEditingOrder(null);
    setOrderForm({
      status: '',
      tracking_number: ''
    });
  };

  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await axios.put(`${API}/admin/pickup-locations/${editingLocation.id}`, locationForm);
        toast.success('Ubicación actualizada');
      } else {
        await axios.post(`${API}/admin/pickup-locations`, locationForm);
        toast.success('Ubicación creada');
      }
      setIsLocationDialogOpen(false);
      resetLocationForm();
      fetchPickupLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar ubicación');
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta ubicación?')) return;
    
    try {
      await axios.delete(`${API}/admin/pickup-locations/${locationId}`);
      toast.success('Ubicación eliminada');
      fetchPickupLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar ubicación');
    }
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setLocationForm({
      name: location.name,
      address: location.address,
      city: location.city,
      phone: location.phone || '',
      active: location.active
    });
    setIsLocationDialogOpen(true);
  };

  const resetLocationForm = () => {
    setEditingLocation(null);
    setLocationForm({
      name: '',
      address: '',
      city: '',
      phone: '',
      active: true
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-600/20 border-yellow-600/50 text-yellow-400',
      processing: 'bg-blue-600/20 border-blue-600/50 text-blue-400',
      shipped: 'bg-purple-600/20 border-purple-600/50 text-purple-400',
      delivered: 'bg-green-600/20 border-green-600/50 text-green-400',
      cancelled: 'bg-red-600/20 border-red-600/50 text-red-400'
    };
    return colors[status] || 'bg-gray-600/20 border-gray-600/50 text-gray-400';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      processing: 'Procesando',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    };
    return labels[status] || status;
  };

  const calculateFinalPrice = (price, discount) => {
    if (discount > 0) {
      return price * (1 - discount / 100);
    }
    return price;
  };

  // WhatsApp Leads Functions
  const fetchWhatsAppLeads = async () => {
    try {
      const response = await axios.get(`${API}/admin/whatsapp-leads`);
      setWhatsappLeads(response.data);
    } catch (error) {
      console.error('Error fetching WhatsApp leads:', error);
    }
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setLeadForm({
      items: lead.items.map(item => ({ ...item })),
      notes: lead.notes || ''
    });
    setIsLeadDialogOpen(true);
  };

  const handleLeadItemChange = (index, field, value) => {
    const updatedItems = [...leadForm.items];
    updatedItems[index][field] = parseFloat(value) || 0;
    setLeadForm({ ...leadForm, items: updatedItems });
  };

  const handleSaveLead = async (e) => {
    e.preventDefault();
    await handleUpdateLeadItems(editingLead.id, leadForm.items, leadForm.notes);
  };

  const resetLeadForm = () => {
    setEditingLead(null);
    setLeadForm({
      items: [],
      notes: ''
    });
  };

  const handleUpdateLeadItems = async (leadId, updatedItems, notes) => {
    try {
      await axios.put(`${API}/admin/whatsapp-leads/${leadId}`, {
        items: updatedItems,
        notes: notes
      });
      toast.success('Lead actualizado');
      setIsLeadDialogOpen(false);
      setEditingLead(null);
      fetchWhatsAppLeads();
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar lead');
    }
  };

  const handleApproveLead = async (leadId) => {
    try {
      await axios.post(`${API}/admin/whatsapp-leads/${leadId}/approve`);
      toast.success('Lead aprobado y convertido en orden');
      fetchWhatsAppLeads();
      fetchOrders();
      fetchProducts();
    } catch (error) {
      console.error('Error approving lead:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar lead');
    }
  };

  const handleDisapproveLead = async (leadId) => {
    try {
      await axios.post(`${API}/admin/whatsapp-leads/${leadId}/disapprove`);
      toast.success('Lead desaprobado');
      fetchWhatsAppLeads();
    } catch (error) {
      console.error('Error disapproving lead:', error);
      toast.error(error.response?.data?.detail || 'Error al desaprobar lead');
    }
  };

  const handleConfirmLeadAction = async () => {
    const { type, leadId } = leadConfirm;
    setLeadConfirm({ open: false, type: null, leadId: null });
    if (type === 'approve') {
      await handleApproveLead(leadId);
    } else if (type === 'disapprove') {
      await handleDisapproveLead(leadId);
    }
  };

  const getLeadStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-600/20 border-yellow-600/50 text-yellow-400',
      approved: 'bg-green-600/20 border-green-600/50 text-green-400',
      disapproved: 'bg-red-600/20 border-red-600/50 text-red-400'
    };
    return colors[status] || 'bg-gray-600/20 border-gray-600/50 text-gray-400';
  };

  const getLeadStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      approved: 'Aprobada',
      disapproved: 'Desaprobada'
    };
    return labels[status] || status;
  };

  // User Management Functions
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const applyUserFilters = () => {
    let filtered = [...users];

    if (userSearchTerm) {
      const searchLower = userSearchTerm.toLowerCase();
      filtered = filtered.filter(user => {
        const email = (user.email || '').toLowerCase();
        const name = (user.name || '').toLowerCase();
        return email.includes(searchLower) || name.includes(searchLower);
      });
    }

    if (userRoleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === userRoleFilter);
    }

    setFilteredUsers(filtered);
  };

  useEffect(() => {
    applyUserFilters();
  }, [userSearchTerm, userRoleFilter, users]);

  const clearUserFilters = () => {
    setUserSearchTerm('');
    setUserRoleFilter('all');
  };

  const activeUserFiltersCount = () => {
    let count = 0;
    if (userSearchTerm) count++;
    if (userRoleFilter !== 'all') count++;
    return count;
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setUserForm({ email: '', name: '', password: '', role: 'user' });
    setIsUserDialogOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role
    });
    setIsUserDialogOpen(true);
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update user
        const updateData = {
          name: userForm.name,
          role: userForm.role
        };
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        await axios.put(`${API}/admin/users/${editingUser.id}`, updateData);
        toast.success('Usuario actualizado');
      } else {
        // Create new user
        await axios.post(`${API}/admin/users`, userForm);
        toast.success('Usuario creado');
      }
      setIsUserDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const resetUserForm = () => {
    setEditingUser(null);
    setUserForm({ email: '', name: '', password: '', role: 'user' });
  };

  return (
    <div className="min-h-screen py-12" data-testid="admin-panel">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-testid="admin-title">
          Panel de Administración
        </h1>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="glass rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 mb-2">Total Productos</p>
                <p className="text-3xl font-bold text-white" data-testid="total-products">{stats.totalProducts}</p>
              </div>
              <Package size={48} className="text-purple-400" />
            </div>
          </div>
          
          <div className="glass rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 mb-2">Total Órdenes</p>
                <p className="text-3xl font-bold text-white" data-testid="total-orders">{stats.totalOrders}</p>
              </div>
              <ShoppingBag size={48} className="text-purple-400" />
            </div>
          </div>
          
          <div className="glass rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 mb-2">Ingresos Totales</p>
                <p className="text-3xl font-bold text-white" data-testid="total-revenue">{formatCurrency(stats.totalRevenue)}</p>
              </div>
              <DollarSign size={48} className="text-purple-400" />
            </div>
          </div>
        </div>

        {/* Tabs for Products and Categories */}
        <Tabs defaultValue="products" className="w-full">
          <TabsList className="grid w-full grid-cols-6 bg-gray-800 mb-8">
            <TabsTrigger value="products">Productos</TabsTrigger>
            <TabsTrigger value="categories">Categorías</TabsTrigger>
            <TabsTrigger value="orders">Órdenes</TabsTrigger>
            <TabsTrigger value="locations">Ubicaciones</TabsTrigger>
            <TabsTrigger value="whatsapp">Ventas WhatsApp</TabsTrigger>
            <TabsTrigger value="users">Usuarios</TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="glass rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <Box className="mr-2" /> Gestionar Productos
                </h2>
                <Dialog open={isProductDialogOpen} onOpenChange={(open) => {
                  setIsProductDialogOpen(open);
                  if (!open) resetProductForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-product-button">
                      <Plus className="mr-2" /> Agregar Producto
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-purple-900/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-2xl">
                        {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleProductSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="name">Nombre</Label>
                        <Input
                          id="name"
                          value={productForm.name}
                          onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                          required
                          className="bg-gray-800 border-purple-900/50"
                          data-testid="product-name-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="description">Descripción</Label>
                        <Textarea
                          id="description"
                          value={productForm.description}
                          onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                          required
                          className="bg-gray-800 border-purple-900/50"
                          data-testid="product-description-input"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="price">Precio</Label>
                          <Input
                            id="price"
                            type="number"
                            step="0.01"
                            value={productForm.price}
                            onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                            required
                            className="bg-gray-800 border-purple-900/50"
                            data-testid="product-price-input"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="discount">Descuento (%)</Label>
                          <Input
                            id="discount"
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={productForm.discount_percentage}
                            onChange={(e) => setProductForm({ ...productForm, discount_percentage: e.target.value })}
                            className="bg-gray-800 border-purple-900/50"
                            data-testid="product-discount-input"
                          />
                        </div>
                      </div>

                      {parseFloat(productForm.discount_percentage) > 0 && parseFloat(productForm.price) > 0 && (
                        <div className="bg-green-600/20 border border-green-600/50 rounded-lg p-3">
                          <p className="text-green-400 text-sm">
                            Precio final: <span className="font-bold">{formatCurrency(calculateFinalPrice(parseFloat(productForm.price), parseFloat(productForm.discount_percentage)))}</span>
                            {' '}(Ahorras {formatCurrency(parseFloat(productForm.price) - calculateFinalPrice(parseFloat(productForm.price), parseFloat(productForm.discount_percentage)))})
                          </p>
                        </div>
                      )}
                      
                      <div>
                        <Label htmlFor="stock">Stock</Label>
                        <Input
                          id="stock"
                          type="number"
                          value={productForm.stock}
                          onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                          required
                          className="bg-gray-800 border-purple-900/50"
                          data-testid="product-stock-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="category">Categoría</Label>
                        <Select 
                          value={productForm.category} 
                          onValueChange={(value) => setProductForm({ ...productForm, category: value })}
                        >
                          <SelectTrigger className="bg-gray-800 border-purple-900/50 text-white" data-testid="product-category-select">
                            <SelectValue placeholder="Selecciona una categoría" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 border-purple-900/50 text-white">
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.name}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="image">URL de Imagen</Label>
                        <Input
                          id="image"
                          value={productForm.image}
                          onChange={(e) => setProductForm({ ...productForm, image: e.target.value })}
                          required
                          className="bg-gray-800 border-purple-900/50"
                          data-testid="product-image-input"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="featured"
                          checked={productForm.featured}
                          onCheckedChange={(checked) => setProductForm({ ...productForm, featured: checked })}
                          data-testid="product-featured-switch"
                        />
                        <Label htmlFor="featured">Producto Destacado</Label>
                      </div>
                      
                      <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" data-testid="save-product-button">
                        {editingProduct ? 'Actualizar' : 'Crear'} Producto
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Product Filters */}
              <div className="bg-gray-800/50 rounded-lg p-4 mb-6 border border-purple-900/30">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Filter size={20} className="text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">Filtros</h3>
                    {activeProductFiltersCount() > 0 && (
                      <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                        {activeProductFiltersCount()}
                      </span>
                    )}
                  </div>
                  {activeProductFiltersCount() > 0 && (
                    <Button
                      onClick={clearProductFilters}
                      variant="outline"
                      size="sm"
                      className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                    >
                      <X size={16} className="mr-1" /> Limpiar Filtros
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Search Filter */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Buscar</Label>
                    <div className="relative">
                      <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Nombre o descripción..."
                        value={productSearchTerm}
                        onChange={(e) => setProductSearchTerm(e.target.value)}
                        className="bg-gray-800 border-purple-900/50 pl-10 text-white"
                      />
                    </div>
                  </div>

                  {/* Category Filter */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Categoría</Label>
                    <Select 
                      value={productCategoryFilter} 
                      onValueChange={(value) => setProductCategoryFilter(value)}
                    >
                      <SelectTrigger className="bg-gray-800 border-purple-900/50 text-white">
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-purple-900/50 text-white">
                        <SelectItem value="all">Todas las categorías</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Toggle Filters */}
                  <div className="space-y-3">
                    <Label className="text-gray-300">Opciones</Label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="filter-discount"
                          checked={showOnlyDiscounted}
                          onCheckedChange={setShowOnlyDiscounted}
                        />
                        <Label htmlFor="filter-discount" className="text-sm text-gray-300 cursor-pointer">
                          Con descuento
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="filter-featured"
                          checked={showOnlyFeatured}
                          onCheckedChange={setShowOnlyFeatured}
                        />
                        <Label htmlFor="filter-featured" className="text-sm text-gray-300 cursor-pointer">
                          Destacados
                        </Label>
                      </div>
                    </div>
                  </div>

                  {/* Stock Filter */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Stock</Label>
                    <div className="flex items-center space-x-2 pt-2">
                      <Switch
                        id="filter-low-stock"
                        checked={showLowStock}
                        onCheckedChange={setShowLowStock}
                      />
                      <Label htmlFor="filter-low-stock" className="text-sm text-gray-300 cursor-pointer">
                        Stock bajo (&lt;10)
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Results Summary */}
                <div className="mt-4 pt-4 border-t border-purple-900/30">
                  <p className="text-sm text-gray-400">
                    Mostrando <span className="font-bold text-purple-400">{filteredProducts.length}</span> de <span className="font-bold text-white">{products.length}</span> productos
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map((product) => {
                  const finalPrice = calculateFinalPrice(product.price, product.discount_percentage || 0);
                  const hasDiscount = product.discount_percentage > 0;
                  
                  return (
                    <div key={product.id} className="bg-gray-900/50 rounded-xl p-4 border border-purple-900/30" data-testid="admin-product-card">
                      <div className="relative">
                        <img src={product.image} alt={product.name} className="w-full h-48 object-cover rounded-lg mb-4" />
                        {hasDiscount && (
                          <div className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                            -{product.discount_percentage}%
                          </div>
                        )}
                      </div>
                      <h3 className="text-white font-bold mb-2" data-testid="admin-product-name">{product.name}</h3>
                      <p className="text-gray-400 text-sm mb-2 line-clamp-2">{product.description}</p>
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          {hasDiscount ? (
                            <div>
                              <span className="text-gray-500 line-through text-sm">{formatCurrency(product.price)}</span>
                              <span className="text-purple-400 font-bold ml-2" data-testid="admin-product-price">{formatCurrency(finalPrice)}</span>
                            </div>
                          ) : (
                            <span className="text-purple-400 font-bold" data-testid="admin-product-price">{formatCurrency(product.price)}</span>
                          )}
                        </div>
                        <span className="text-gray-400 text-sm" data-testid="admin-product-stock">Stock: {product.stock}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleEditProduct(product)}
                          variant="outline"
                          size="sm"
                          className="flex-1 border-purple-600 text-purple-400 hover:bg-purple-600/10"
                          data-testid="edit-product-button"
                        >
                          <Edit size={16} className="mr-1" /> Editar
                        </Button>
                        <Button
                          onClick={() => handleDeleteProduct(product.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-600 text-red-400 hover:bg-red-600/10"
                          data-testid="delete-product-button"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <div className="glass rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <Tag className="mr-2" /> Gestionar Categorías
                </h2>
                <Dialog open={isCategoryDialogOpen} onOpenChange={(open) => {
                  setIsCategoryDialogOpen(open);
                  if (!open) resetCategoryForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-category-button">
                      <Plus className="mr-2" /> Agregar Categoría
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-purple-900/50 text-white">
                    <DialogHeader>
                      <DialogTitle className="text-2xl">
                        {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCategorySubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="cat-name">Nombre</Label>
                        <Input
                          id="cat-name"
                          value={categoryForm.name}
                          onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                          required
                          className="bg-gray-800 border-purple-900/50"
                          data-testid="category-name-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="cat-description">Descripción</Label>
                        <Textarea
                          id="cat-description"
                          value={categoryForm.description}
                          onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                          className="bg-gray-800 border-purple-900/50"
                          data-testid="category-description-input"
                        />
                      </div>
                      
                      <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" data-testid="save-category-button">
                        {editingCategory ? 'Actualizar' : 'Crear'} Categoría
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.map((category) => (
                  <div key={category.id} className="bg-gray-900/50 rounded-xl p-6 border border-purple-900/30" data-testid="category-card">
                    <div className="flex items-start justify-between mb-4">
                      <div className="bg-purple-600/20 p-3 rounded-lg">
                        <Tag size={24} className="text-purple-400" />
                      </div>
                    </div>
                    <h3 className="text-white font-bold text-xl mb-2" data-testid="category-name">{category.name}</h3>
                    <p className="text-gray-400 text-sm mb-4" data-testid="category-description">{category.description || 'Sin descripción'}</p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleEditCategory(category)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-purple-600 text-purple-400 hover:bg-purple-600/10"
                        data-testid="edit-category-button"
                      >
                        <Edit size={16} className="mr-1" /> Editar
                      </Button>
                      <Button
                        onClick={() => handleDeleteCategory(category.id)}
                        variant="outline"
                        size="sm"
                        className="border-red-600 text-red-400 hover:bg-red-600/10"
                        data-testid="delete-category-button"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {categories.length === 0 && (
                <div className="text-center py-12">
                  <Tag size={64} className="text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">No hay categorías creadas</p>
                  <p className="text-gray-500 text-sm">Crea tu primera categoría para organizar tus productos</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="glass rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <ShoppingBag className="mr-2" /> Gestionar Órdenes
                </h2>
              </div>

              {/* Order Filters */}
              <div className="bg-gray-800/50 rounded-lg p-4 mb-6 border border-purple-900/30">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Filter size={20} className="text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">Filtros</h3>
                    {activeOrderFiltersCount() > 0 && (
                      <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                        {activeOrderFiltersCount()}
                      </span>
                    )}
                  </div>
                  {activeOrderFiltersCount() > 0 && (
                    <Button
                      onClick={clearOrderFilters}
                      variant="outline"
                      size="sm"
                      className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                    >
                      <X size={16} className="mr-1" /> Limpiar Filtros
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* Search Filter */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Buscar</Label>
                    <div className="relative">
                      <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Email, nombre o ID..."
                        value={orderSearchTerm}
                        onChange={(e) => setOrderSearchTerm(e.target.value)}
                        className="bg-gray-800 border-purple-900/50 pl-10 text-white"
                      />
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Estado</Label>
                    <Select 
                      value={orderStatusFilter} 
                      onValueChange={(value) => setOrderStatusFilter(value)}
                    >
                      <SelectTrigger className="bg-gray-800 border-purple-900/50 text-white">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-purple-900/50 text-white">
                        <SelectItem value="all">Todos los estados</SelectItem>
                        <SelectItem value="pending">⏳ Pendiente</SelectItem>
                        <SelectItem value="processing">🔄 Procesando</SelectItem>
                        <SelectItem value="shipped">📦 Enviado</SelectItem>
                        <SelectItem value="delivered">✅ Entregado</SelectItem>
                        <SelectItem value="cancelled">❌ Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Delivery Type Filter */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Tipo de Entrega</Label>
                    <Select 
                      value={orderDeliveryTypeFilter} 
                      onValueChange={(value) => setOrderDeliveryTypeFilter(value)}
                    >
                      <SelectTrigger className="bg-gray-800 border-purple-900/50 text-white">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-purple-900/50 text-white">
                        <SelectItem value="all">Todos los tipos</SelectItem>
                        <SelectItem value="delivery">🚚 Envío a domicilio</SelectItem>
                        <SelectItem value="pickup">📦 Retiro en tienda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date From */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Desde</Label>
                    <Input
                      type="date"
                      value={orderDateFrom}
                      onChange={(e) => setOrderDateFrom(e.target.value)}
                      className="bg-gray-800 border-purple-900/50 text-white"
                    />
                  </div>

                  {/* Date To */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Hasta</Label>
                    <Input
                      type="date"
                      value={orderDateTo}
                      onChange={(e) => setOrderDateTo(e.target.value)}
                      className="bg-gray-800 border-purple-900/50 text-white"
                    />
                  </div>
                </div>

                {/* Results Summary */}
                <div className="mt-4 pt-4 border-t border-purple-900/30">
                  <p className="text-sm text-gray-400">
                    Mostrando <span className="font-bold text-purple-400">{filteredOrders.length}</span> de <span className="font-bold text-white">{orders.length}</span> órdenes
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {filteredOrders.map((order) => (
                  <div key={order.id} className="bg-gray-900/50 rounded-xl p-6 border border-purple-900/30" data-testid="order-card">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-white font-bold text-lg mb-2">
                          Orden #{order.id.slice(0, 8)}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {new Date(order.created_at).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className={`inline-block px-4 py-2 rounded-full border ${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-gray-400 text-sm">Cliente</p>
                        <p className="text-white">
                          {order.guest_name || 'Usuario registrado'}
                        </p>
                        <p className="text-gray-400 text-sm">
                          {order.guest_email || 'Ver en detalles'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Entrega</p>
                        <p className="text-white">
                          {order.delivery_type === 'pickup' ? '📦 Retiro en tienda' : '🚚 Envío a domicilio'}
                        </p>
                        {order.tracking_number && (
                          <p className="text-purple-400 text-sm">
                            Tracking: {order.tracking_number}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-purple-900/30 pt-4 mb-4">
                      <p className="text-gray-400 text-sm mb-2">Productos ({order.items.length})</p>
                      <div className="space-y-2">
                        {order.items.slice(0, 2).map((item, idx) => (
                          <p key={idx} className="text-white text-sm">
                            {item.quantity}x {item.product_name} - {formatCurrency(item.price)}
                          </p>
                        ))}
                        {order.items.length > 2 && (
                          <p className="text-gray-400 text-sm">
                            ... y {order.items.length - 2} más
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="text-2xl font-bold text-purple-400">
                        Total: {formatCurrency(order.total)}
                      </div>
                      <Button
                        onClick={() => handleEditOrder(order)}
                        variant="outline"
                        size="sm"
                        className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                        data-testid="edit-order-button"
                      >
                        <Edit size={16} className="mr-1" /> Actualizar Estado
                      </Button>
                    </div>
                  </div>
                ))}

                {filteredOrders.length === 0 && orders.length > 0 && (
                  <div className="text-center py-12">
                    <Filter size={64} className="text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">No se encontraron órdenes con los filtros aplicados</p>
                    <Button
                      onClick={clearOrderFilters}
                      variant="outline"
                      className="mt-4 border-purple-600 text-purple-400 hover:bg-purple-600/10"
                    >
                      Limpiar Filtros
                    </Button>
                  </div>
                )}

                {orders.length === 0 && (
                  <div className="text-center py-12">
                    <ShoppingBag size={64} className="text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">No hay órdenes aún</p>
                  </div>
                )}
              </div>
            </div>

            {/* Order Status Dialog */}
            <Dialog open={isOrderDialogOpen} onOpenChange={setIsOrderDialogOpen}>
              <DialogContent className="bg-gray-900 border-purple-900/50 text-white">
                <DialogHeader>
                  <DialogTitle className="text-2xl">
                    Actualizar Estado de Orden #{editingOrder?.id.slice(0, 8)}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleOrderSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="status">Estado</Label>
                    <Select value={orderForm.status} onValueChange={(value) => setOrderForm({ ...orderForm, status: value })}>
                      <SelectTrigger className="bg-gray-800 border-purple-900/50 text-white" data-testid="order-status-select">
                        <SelectValue placeholder="Selecciona un estado" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-purple-900/50 text-white">
                        <SelectItem value="pending">Pendiente</SelectItem>
                        <SelectItem value="processing">Procesando</SelectItem>
                        <SelectItem value="shipped">Enviado</SelectItem>
                        <SelectItem value="delivered">Entregado</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="tracking">Número de Seguimiento</Label>
                    <Input
                      id="tracking"
                      value={orderForm.tracking_number}
                      onChange={(e) => setOrderForm({ ...orderForm, tracking_number: e.target.value })}
                      className="bg-gray-800 border-purple-900/50"
                      placeholder="SKS20251115ABCD1234"
                      data-testid="tracking-number-input"
                    />
                  </div>
                  
                  <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" data-testid="save-order-button">
                    Actualizar Orden
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Pickup Locations Tab */}
          <TabsContent value="locations">
            <div className="glass rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <MapPin className="mr-2" /> Ubicaciones de Retiro
                </h2>
                <Dialog open={isLocationDialogOpen} onOpenChange={(open) => {
                  setIsLocationDialogOpen(open);
                  if (!open) resetLocationForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-location-button">
                      <Plus className="mr-2" /> Agregar Ubicación
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-purple-900/50 text-white">
                    <DialogHeader>
                      <DialogTitle className="text-2xl">
                        {editingLocation ? 'Editar Ubicación' : 'Nueva Ubicación'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleLocationSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="loc-name">Nombre</Label>
                        <Input
                          id="loc-name"
                          value={locationForm.name}
                          onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                          required
                          className="bg-gray-800 border-purple-900/50"
                          data-testid="location-name-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="loc-address">Dirección</Label>
                        <Input
                          id="loc-address"
                          value={locationForm.address}
                          onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
                          required
                          className="bg-gray-800 border-purple-900/50"
                          data-testid="location-address-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="loc-city">Ciudad</Label>
                        <Input
                          id="loc-city"
                          value={locationForm.city}
                          onChange={(e) => setLocationForm({ ...locationForm, city: e.target.value })}
                          required
                          className="bg-gray-800 border-purple-900/50"
                          data-testid="location-city-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="loc-phone">Teléfono</Label>
                        <Input
                          id="loc-phone"
                          value={locationForm.phone}
                          onChange={(e) => setLocationForm({ ...locationForm, phone: e.target.value })}
                          className="bg-gray-800 border-purple-900/50"
                          placeholder="+51 1 234-5678"
                          data-testid="location-phone-input"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="loc-active"
                          checked={locationForm.active}
                          onCheckedChange={(checked) => setLocationForm({ ...locationForm, active: checked })}
                          data-testid="location-active-switch"
                        />
                        <Label htmlFor="loc-active">Ubicación Activa</Label>
                      </div>
                      
                      <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" data-testid="save-location-button">
                        {editingLocation ? 'Actualizar' : 'Crear'} Ubicación
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Location Filters */}
              <div className="bg-gray-800/50 rounded-lg p-4 mb-6 border border-purple-900/30">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Filter size={20} className="text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">Filtros</h3>
                    {activeLocationFiltersCount() > 0 && (
                      <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                        {activeLocationFiltersCount()}
                      </span>
                    )}
                  </div>
                  {activeLocationFiltersCount() > 0 && (
                    <Button
                      onClick={clearLocationFilters}
                      variant="outline"
                      size="sm"
                      className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                      data-testid="clear-location-filters-button"
                    >
                      <X size={16} className="mr-1" /> Limpiar Filtros
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Buscar</Label>
                    <div className="relative">
                      <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Nombre, dirección, ciudad o teléfono..."
                        value={locationSearchTerm}
                        onChange={(e) => setLocationSearchTerm(e.target.value)}
                        className="bg-gray-800 border-purple-900/50 pl-10 text-white"
                        data-testid="location-search-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Estado</Label>
                    <Select value={locationStatusFilter} onValueChange={(value) => setLocationStatusFilter(value)}>
                      <SelectTrigger className="bg-gray-800 border-purple-900/50 text-white" data-testid="location-status-filter">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-purple-900/50 text-white">
                        <SelectItem value="all">Todos los estados</SelectItem>
                        <SelectItem value="active">✅ Activo</SelectItem>
                        <SelectItem value="inactive">⛔ Inactivo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-purple-900/30">
                  <p className="text-sm text-gray-400">
                    Mostrando <span className="font-bold text-purple-400">{filteredLocations.length}</span> de <span className="font-bold text-white">{pickupLocations.length}</span> ubicaciones
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredLocations.map((location) => (
                  <div key={location.id} className="bg-gray-900/50 rounded-xl p-6 border border-purple-900/30" data-testid="location-card">
                    <div className="flex items-start justify-between mb-4">
                      <div className="bg-purple-600/20 p-3 rounded-lg">
                        <MapPin size={24} className="text-purple-400" />
                      </div>
                      <div className={`inline-block px-3 py-1 rounded-full text-xs ${location.active ? 'bg-green-600/20 text-green-400' : 'bg-gray-600/20 text-gray-400'}`}>
                        {location.active ? 'Activo' : 'Inactivo'}
                      </div>
                    </div>
                    <h3 className="text-white font-bold text-xl mb-2" data-testid="location-name">{location.name}</h3>
                    <p className="text-gray-400 text-sm mb-1">{location.address}</p>
                    <p className="text-gray-400 text-sm mb-2">{location.city}</p>
                    {location.phone && (
                      <p className="text-purple-400 text-sm mb-4">📞 {location.phone}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleEditLocation(location)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-purple-600 text-purple-400 hover:bg-purple-600/10"
                        data-testid="edit-location-button"
                      >
                        <Edit size={16} className="mr-1" /> Editar
                      </Button>
                      <Button
                        onClick={() => handleDeleteLocation(location.id)}
                        variant="outline"
                        size="sm"
                        className="border-red-600 text-red-400 hover:bg-red-600/10"
                        data-testid="delete-location-button"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {filteredLocations.length === 0 && (
                <div className="text-center py-12">
                  <MapPin size={64} className="text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">
                    {pickupLocations.length === 0 ? 'No hay ubicaciones creadas' : 'No hay ubicaciones que coincidan con los filtros'}
                  </p>
                  <p className="text-gray-500 text-sm">Crea puntos de retiro para que los clientes recojan sus pedidos</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* WhatsApp Leads Tab */}
          <TabsContent value="whatsapp">
            <div className="glass rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <MessageCircle className="mr-2 text-green-400" /> Ventas Potenciales por WhatsApp
                </h2>
                
                {/* Edit Lead Dialog */}
                <Dialog open={isLeadDialogOpen} onOpenChange={(open) => {
                  setIsLeadDialogOpen(open);
                  if (!open) resetLeadForm();
                }}>
                  <DialogContent className="bg-gray-900 border-purple-900/50 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-2xl">Editar Venta Potencial</DialogTitle>
                    </DialogHeader>
                    {editingLead && (
                      <form onSubmit={handleSaveLead} className="space-y-4">
                        <div className="bg-gray-800/50 p-4 rounded-lg mb-4">
                          <h3 className="text-white font-semibold mb-2">Cliente</h3>
                          <p className="text-gray-400 text-sm">{editingLead.customer_name}</p>
                          <p className="text-gray-400 text-sm">{editingLead.customer_email}</p>
                          <p className="text-green-400 text-sm">{editingLead.customer_whatsapp}</p>
                        </div>

                        <div>
                          <Label className="text-lg font-semibold mb-3 block">Productos</Label>
                          <p className="text-xs text-gray-400 mb-3">
                            <strong>Nota:</strong> Producto y categoría no se pueden editar. Solo puedes modificar cantidad, precio y descuento.
                          </p>
                          <div className="space-y-3">
                            {leadForm.items.map((item, index) => (
                              <div key={index} className="bg-gray-800/30 p-4 rounded-lg">
                                <div className="mb-3">
                                  <p className="text-white font-medium">{item.product_name}</p>
                                  <p className="text-gray-500 text-sm">Categoría: {item.product_category}</p>
                                </div>
                                
                                <div className="grid grid-cols-3 gap-3">
                                  <div>
                                    <Label htmlFor={`quantity-${index}`} className="text-xs">Cantidad</Label>
                                    <Input
                                      id={`quantity-${index}`}
                                      type="number"
                                      min="1"
                                      value={item.quantity}
                                      onChange={(e) => handleLeadItemChange(index, 'quantity', e.target.value)}
                                      className="bg-gray-800 border-purple-900/50"
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor={`price-${index}`} className="text-xs">Precio</Label>
                                    <Input
                                      id={`price-${index}`}
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={item.price}
                                      onChange={(e) => handleLeadItemChange(index, 'price', e.target.value)}
                                      className="bg-gray-800 border-purple-900/50"
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor={`discount-${index}`} className="text-xs">Descuento (%)</Label>
                                    <Input
                                      id={`discount-${index}`}
                                      type="number"
                                      step="0.1"
                                      min="0"
                                      max="100"
                                      value={item.discount_percentage}
                                      onChange={(e) => handleLeadItemChange(index, 'discount_percentage', e.target.value)}
                                      className="bg-gray-800 border-purple-900/50"
                                    />
                                  </div>
                                </div>
                                
                                <div className="mt-2 text-right">
                                  <span className="text-sm text-gray-400">Subtotal: </span>
                                  <span className="text-purple-400 font-semibold">
                                    ${(item.price * (1 - item.discount_percentage / 100) * item.quantity).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          <div className="mt-4 bg-purple-600/10 border border-purple-600/30 rounded-lg p-3">
                            <div className="flex justify-between items-center">
                              <span className="text-white font-bold text-lg">Total:</span>
                              <span className="text-purple-400 font-bold text-xl">
                                ${leadForm.items.reduce((sum, item) => 
                                  sum + (item.price * (1 - item.discount_percentage / 100) * item.quantity), 0
                                ).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="lead-notes">Notas (Opcional)</Label>
                          <Textarea
                            id="lead-notes"
                            value={leadForm.notes}
                            onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })}
                            className="bg-gray-800 border-purple-900/50"
                            placeholder="Agregar notas sobre esta venta potencial..."
                            rows={3}
                          />
                        </div>
                        
                        <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                          Guardar Cambios
                        </Button>
                      </form>
                    )}
                  </DialogContent>
                </Dialog>
              </div>

              {/* WhatsApp Lead Filters */}
              <div className="bg-gray-800/50 rounded-lg p-4 mb-6 border border-purple-900/30">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Filter size={20} className="text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">Filtros</h3>
                    {activeLeadFiltersCount() > 0 && (
                      <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                        {activeLeadFiltersCount()}
                      </span>
                    )}
                  </div>
                  {activeLeadFiltersCount() > 0 && (
                    <Button
                      onClick={clearLeadFilters}
                      variant="outline"
                      size="sm"
                      className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                      data-testid="clear-lead-filters-button"
                    >
                      <X size={16} className="mr-1" /> Limpiar Filtros
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Buscar</Label>
                    <div className="relative">
                      <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Nombre, email o WhatsApp..."
                        value={leadSearchTerm}
                        onChange={(e) => setLeadSearchTerm(e.target.value)}
                        className="bg-gray-800 border-purple-900/50 pl-10 text-white"
                        data-testid="lead-search-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Estado</Label>
                    <Select value={leadStatusFilter} onValueChange={(value) => setLeadStatusFilter(value)}>
                      <SelectTrigger className="bg-gray-800 border-purple-900/50 text-white" data-testid="lead-status-filter">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-purple-900/50 text-white">
                        <SelectItem value="all">Todos los estados</SelectItem>
                        <SelectItem value="pending">⏳ Pendiente</SelectItem>
                        <SelectItem value="approved">✅ Aprobada</SelectItem>
                        <SelectItem value="disapproved">❌ Desaprobada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Desde</Label>
                    <Input
                      type="date"
                      value={leadDateFrom}
                      onChange={(e) => setLeadDateFrom(e.target.value)}
                      className="bg-gray-800 border-purple-900/50 text-white"
                      data-testid="lead-date-from"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Hasta</Label>
                    <Input
                      type="date"
                      value={leadDateTo}
                      onChange={(e) => setLeadDateTo(e.target.value)}
                      className="bg-gray-800 border-purple-900/50 text-white"
                      data-testid="lead-date-to"
                    />
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-purple-900/30">
                  <p className="text-sm text-gray-400">
                    Mostrando <span className="font-bold text-purple-400">{filteredLeads.length}</span> de <span className="font-bold text-white">{whatsappLeads.length}</span> ventas
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {filteredLeads.map((lead) => {
                  const isPending = lead.status === 'pending';
                  
                  return (
                    <div key={lead.id} className="bg-gray-900/50 rounded-xl p-6 border border-purple-900/30">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-white font-bold text-lg mb-1">{lead.customer_name}</h3>
                          <p className="text-gray-400 text-sm">📧 {lead.customer_email}</p>
                          <p className="text-green-400 text-sm">📱 {lead.customer_whatsapp}</p>
                          <p className="text-gray-500 text-xs mt-2">
                            Creado: {new Date(lead.created_at).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className={`px-4 py-2 rounded-full border text-sm font-medium ${getLeadStatusColor(lead.status)}`}>
                          {getLeadStatusLabel(lead.status)}
                        </div>
                      </div>

                      {/* Products */}
                      <div className="bg-gray-800/30 rounded-lg p-4 mb-4">
                        <h4 className="text-white font-semibold mb-3">Productos:</h4>
                        <div className="space-y-2">
                          {lead.items.map((item, idx) => {
                            const finalPrice = item.price * (1 - item.discount_percentage / 100);
                            return (
                              <div key={idx} className="flex justify-between items-center text-sm">
                                <div>
                                  <span className="text-white">{item.quantity}x {item.product_name}</span>
                                  <span className="text-gray-500 ml-2">({item.product_category})</span>
                                  {item.discount_percentage > 0 && (
                                    <span className="text-green-400 ml-2 text-xs">-{item.discount_percentage}%</span>
                                  )}
                                </div>
                                <div className="text-right">
                                  {item.discount_percentage > 0 ? (
                                    <>
                                      <span className="text-gray-500 line-through text-xs mr-2">{formatCurrency(item.price)}</span>
                                      <span className="text-purple-400 font-semibold">{formatCurrency(finalPrice)}</span>
                                    </>
                                  ) : (
                                    <span className="text-purple-400 font-semibold">{formatCurrency(item.price)}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="border-t border-gray-700 mt-3 pt-3 flex justify-between items-center">
                          <span className="text-white font-bold">Total:</span>
                          <span className="text-purple-400 font-bold text-lg">{formatCurrency(lead.total)}</span>
                        </div>
                      </div>

                      {/* Notes */}
                      {lead.notes && (
                        <div className="bg-blue-600/10 border border-blue-600/30 rounded-lg p-3 mb-4">
                          <p className="text-blue-300 text-sm"><strong>Notas:</strong> {lead.notes}</p>
                        </div>
                      )}

                      {/* Order ID if approved */}
                      {lead.order_id && (
                        <div className="bg-green-600/10 border border-green-600/30 rounded-lg p-3 mb-4">
                          <p className="text-green-300 text-sm"><strong>ID de Orden:</strong> {lead.order_id}</p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      {isPending && (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleEditLead(lead)}
                            variant="outline"
                            size="sm"
                            className="flex-1 border-purple-600 text-purple-400 hover:bg-purple-600/10"
                          >
                            <Edit size={16} className="mr-1" /> Editar Precios/Cantidad
                          </Button>
                          <Button
                            onClick={() => setLeadConfirm({ open: true, type: 'approve', leadId: lead.id })}
                            variant="outline"
                            size="sm"
                            data-testid="approve-lead-button"
                            className="flex-1 border-green-600 text-green-400 hover:bg-green-600/10"
                          >
                            ✓ Aprobar
                          </Button>
                          <Button
                            onClick={() => setLeadConfirm({ open: true, type: 'disapprove', leadId: lead.id })}
                            variant="outline"
                            size="sm"
                            data-testid="disapprove-lead-button"
                            className="border-red-600 text-red-400 hover:bg-red-600/10"
                          >
                            ✕ Desaprobar
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {filteredLeads.length === 0 && (
                  <div className="text-center py-12">
                    <MessageCircle size={64} className="text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">
                      {whatsappLeads.length === 0 ? 'No hay ventas por WhatsApp' : 'No hay ventas que coincidan con los filtros'}
                    </p>
                    <p className="text-gray-500 text-sm">Las solicitudes por WhatsApp aparecerán aquí</p>
                  </div>
                )}
              </div>

              <AlertDialog open={leadConfirm.open} onOpenChange={(open) => setLeadConfirm((prev) => ({ ...prev, open }))}>
                <AlertDialogContent className="bg-gray-900 border-purple-900/50 text-white" data-testid="lead-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">
                      {leadConfirm.type === 'approve' ? '¿Aprobar venta por WhatsApp?' : '¿Desaprobar venta por WhatsApp?'}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-gray-400">
                      {leadConfirm.type === 'approve'
                        ? 'Se convertirá en una orden, se descontará el stock de los productos y se actualizarán los totales.'
                        : 'Esta venta potencial será marcada como desaprobada.'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="lead-confirm-cancel" className="bg-gray-800 border-purple-900/50 text-white hover:bg-gray-700 hover:text-white">Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleConfirmLeadAction}
                      data-testid="lead-confirm-accept"
                      className={leadConfirm.type === 'approve' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
                    >
                      {leadConfirm.type === 'approve' ? 'Aprobar' : 'Desaprobar'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="glass rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <Users className="mr-2" /> Gestionar Usuarios
                </h2>
                <Dialog open={isUserDialogOpen} onOpenChange={(open) => {
                  setIsUserDialogOpen(open);
                  if (!open) resetUserForm();
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      onClick={handleCreateUser}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Plus size={20} className="mr-2" /> Crear Usuario
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-purple-900/50 text-white">
                    <DialogHeader>
                      <DialogTitle className="text-2xl">
                        {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUserSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="user-email">Email</Label>
                        <Input
                          id="user-email"
                          type="email"
                          value={userForm.email}
                          onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                          disabled={!!editingUser}
                          required
                          className="bg-gray-800 border-purple-900/50 text-white"
                        />
                      </div>
                      <div>
                        <Label htmlFor="user-name">Nombre</Label>
                        <Input
                          id="user-name"
                          type="text"
                          value={userForm.name}
                          onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                          required
                          className="bg-gray-800 border-purple-900/50 text-white"
                        />
                      </div>
                      <div>
                        <Label htmlFor="user-password">
                          {editingUser ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
                        </Label>
                        <Input
                          id="user-password"
                          type="password"
                          value={userForm.password}
                          onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                          required={!editingUser}
                          className="bg-gray-800 border-purple-900/50 text-white"
                        />
                      </div>
                      <div>
                        <Label htmlFor="user-role">Rol</Label>
                        <Select 
                          value={userForm.role} 
                          onValueChange={(value) => setUserForm({ ...userForm, role: value })}
                        >
                          <SelectTrigger className="bg-gray-800 border-purple-900/50 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 border-purple-900/50 text-white">
                            <SelectItem value="user">Usuario</SelectItem>
                            <SelectItem value="admin">Administrador</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                        {editingUser ? 'Actualizar Usuario' : 'Crear Usuario'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* User Filters */}
              <div className="bg-gray-800/50 rounded-lg p-4 mb-6 border border-purple-900/30">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Filter size={20} className="text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">Filtros</h3>
                    {activeUserFiltersCount() > 0 && (
                      <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                        {activeUserFiltersCount()}
                      </span>
                    )}
                  </div>
                  {activeUserFiltersCount() > 0 && (
                    <Button
                      onClick={clearUserFilters}
                      variant="outline"
                      size="sm"
                      className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                    >
                      <X size={16} className="mr-1" /> Limpiar Filtros
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Buscar</Label>
                    <div className="relative">
                      <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Email o nombre..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        className="bg-gray-800 border-purple-900/50 pl-10 text-white"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Rol</Label>
                    <Select 
                      value={userRoleFilter} 
                      onValueChange={(value) => setUserRoleFilter(value)}
                    >
                      <SelectTrigger className="bg-gray-800 border-purple-900/50 text-white">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-purple-900/50 text-white">
                        <SelectItem value="all">Todos los roles</SelectItem>
                        <SelectItem value="user">👤 Usuario</SelectItem>
                        <SelectItem value="admin">🔑 Administrador</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-purple-900/30">
                  <p className="text-sm text-gray-400">
                    Mostrando <span className="font-bold text-purple-400">{filteredUsers.length}</span> de <span className="font-bold text-white">{users.length}</span> usuarios
                  </p>
                </div>
              </div>

              {/* Users Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-purple-900/30">
                      <th className="text-left text-gray-400 pb-3 px-4">Email</th>
                      <th className="text-left text-gray-400 pb-3 px-4">Nombre</th>
                      <th className="text-left text-gray-400 pb-3 px-4">Rol</th>
                      <th className="text-left text-gray-400 pb-3 px-4">Proveedor</th>
                      <th className="text-left text-gray-400 pb-3 px-4">Fecha de Registro</th>
                      <th className="text-right text-gray-400 pb-3 px-4">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b border-purple-900/20 hover:bg-gray-800/30">
                        <td className="py-4 px-4 text-white">{user.email}</td>
                        <td className="py-4 px-4 text-white">{user.name}</td>
                        <td className="py-4 px-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            user.role === 'admin' 
                              ? 'bg-purple-600/20 border border-purple-600/50 text-purple-400'
                              : 'bg-blue-600/20 border border-blue-600/50 text-blue-400'
                          }`}>
                            {user.role === 'admin' ? '🔑 Admin' : '👤 Usuario'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-gray-400 capitalize">{user.auth_provider}</td>
                        <td className="py-4 px-4 text-gray-400">
                          {new Date(user.created_at).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              onClick={() => handleEditUser(user)}
                              variant="outline"
                              size="sm"
                              className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              onClick={() => handleDeleteUser(user.id)}
                              variant="outline"
                              size="sm"
                              className="border-red-600 text-red-400 hover:bg-red-600/10"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredUsers.length === 0 && users.length > 0 && (
                  <div className="text-center py-12">
                    <Filter size={64} className="text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">No se encontraron usuarios con los filtros aplicados</p>
                    <Button
                      onClick={clearUserFilters}
                      variant="outline"
                      className="mt-4 border-purple-600 text-purple-400 hover:bg-purple-600/10"
                    >
                      Limpiar Filtros
                    </Button>
                  </div>
                )}

                {users.length === 0 && (
                  <div className="text-center py-12">
                    <Users size={64} className="text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">No hay usuarios registrados</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;